/**
 * editor2/ReservationEditor.tsx
 * ReservationEditor v2 メインコンテナコンポーネント
 * hooks とセクションを組み合わせるワイヤリング層（ビジネスロジックは hooks/sections に委譲）
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Snackbar, Alert } from '@mui/material';
import '../../styles/gridCalendar.scss';

// Types
import type { ReservationEditorProps, EditorReservation, MemoBlock } from './types';
import { SAFE_DEFAULTS } from './constants';

// Hooks
import useReservationEditor from './hooks/useReservationEditor';
import useGenericEdit from './hooks/useGenericEdit';
import usePaymentEmail from './hooks/usePaymentEmail';
import useSquareInvoice from './hooks/useSquareInvoice';
import useMemoTimeline from './hooks/useMemoTimeline';
import { useReservationStatusChange } from '../../hooks/useReservationStatusChange';

// Sections
import {
  BasicInfoSection,
  StatusSection,
  ScheduleSection,
  VehicleSection,
  OptionsSection,
  CustomerSection,
  PricingSection,
  CheckFlagsSection,
  CommunicationSection,
  MemoSection,
  StaffSection,
  EmergencySection,
  ColorSection,
  EmailTemplateSection,
  DetailSettingsSection,
} from './sections';

// Modals
import GenericEditDialog from './modals/GenericEditDialog';
import MemoBlockEditModal from './modals/MemoBlockEditModal';
import RecalcModal from './modals/RecalcModal';
import PaymentEmailModal from './modals/PaymentEmailModal';
import JsonDataModal from './modals/JsonDataModal';

// sysApiBase は定数から取得
import { SYS_API_BASE } from '../../constants/calendar';

export default function ReservationEditor({
  open,
  editing,
  disabled,
  onChange,
  onCancel,
  onSave,
  registerNameInputRef,
  reservationStatusList,
  vehicles,
  vehicleTypes,
  onAssignClick,
}: ReservationEditorProps) {
  // --- スライドアニメーション管理 ---
  // editing のID変化を検知して、閉じ→開きを自動制御
  const [panelVisible, setPanelVisible] = useState(open);
  const panelRef = useRef<HTMLDivElement>(null);
  const lastEditingRef = useRef<any>(editing);
  const prevEditingIdRef = useRef<string | null>(null);
  const pendingEditingRef = useRef<any>(null);

  // editing が変わるたびに最新を保持（閉じアニメーション中の表示用）
  useEffect(() => {
    if (editing) {
      lastEditingRef.current = editing;
    }
  }, [editing]);

  useEffect(() => {
    const currentId = editing ? String(editing.id ?? editing.page_id ?? '') : null;
    const prevId = prevEditingIdRef.current;

    if (open && editing) {
      if (prevId && prevId !== currentId) {
        // 別の予約に切り替え → まずスライドアウト、完了後にスライドイン
        pendingEditingRef.current = editing;
        setPanelVisible(false);
      } else if (!panelVisible) {
        // 新規オープン
        prevEditingIdRef.current = currentId;
        setPanelVisible(true);
      } else {
        // 同じ予約の更新（フィールド編集等）
        prevEditingIdRef.current = currentId;
      }
    } else if (!open && panelVisible) {
      prevEditingIdRef.current = null;
      pendingEditingRef.current = null;
      setPanelVisible(false);
    }
  }, [open, editing]);

  // transitionend でスライドアウト完了を検知
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform') return;
      if (!panelVisible && pendingEditingRef.current) {
        // 閉じアニメーション完了 → 待機中の予約でスライドイン
        const pending = pendingEditingRef.current;
        prevEditingIdRef.current = String(pending.id ?? pending.page_id ?? '');
        pendingEditingRef.current = null;
        setPanelVisible(true);
      }
    };

    el.addEventListener('transitionend', handleTransitionEnd);
    return () => el.removeEventListener('transitionend', handleTransitionEnd);
  }, [panelVisible]);

  // --- Derived state ---
  // panelVisible中は editing を、スライドアウト中は lastEditingRef を表示
  const displayEditing = editing ?? lastEditingRef.current;
  const safe: EditorReservation = (displayEditing ?? SAFE_DEFAULTS) as EditorReservation;
  const isNew = Boolean(displayEditing?.__isNew || displayEditing?.__isCopy);
  const sysApiBase = SYS_API_BASE;

  // --- Toast state ---
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'info' | 'success' | 'error'; duration: number | null }>({
    open: false, message: '', severity: 'info', duration: 5000,
  });

  // --- Modal open states ---
  const [memoBlockEditBlock, setMemoBlockEditBlock] = useState<MemoBlock | null>(null);
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);

  // --- Data getter (stable ref for hooks) ---
  const getData = useCallback(() => (editing ?? SAFE_DEFAULTS) as EditorReservation, [editing]);

  // --- Hooks ---
  const editor = useReservationEditor({
    open,
    editing,
    onChange,
    onSave,
    statusOptions: reservationStatusList,
  });

  const genericEdit = useGenericEdit({
    getData,
    onChange,
    statusOptions: reservationStatusList,
  });

  const paymentEmail = usePaymentEmail({
    getData,
    sysApiBase,
  });

  const squareInvoice = useSquareInvoice({
    getData,
    onChange,
    onSave,
    sysApiBase,
  });

  const memoTimeline = useMemoTimeline();

  // --- 予約ステータス即保存フック ---
  const statusChange = useReservationStatusChange({
    reservationStatusList: reservationStatusList || [],
    getReservationData: () => editing,
    onReservationStatusSaved: (reservationId, newStatus) => {
      // UIに反映（フォーム全体は保存しない、ステータスだけ更新）
      onChange?.({ status: String(newStatus) });
      editor.markSaved();
    },
    onToast: (message) => {
      console.log('[StatusChange]', message);
    },
  });

  const handleStatusChange = useCallback(
    (newStatusId: string | number) => {
      if (!editing) return;
      const reservationId = String(editing.id || editing.page_id);
      const vehicleId = editing.vehicle_id ? String(editing.vehicle_id) : null;
      statusChange.requestStatusChange(reservationId, newStatusId, editing.status, vehicleId);
    },
    [editing, statusChange],
  );

  // --- Callbacks ---

  const handleRecalcApply = useCallback(
    (sum: number) => {
      onChange?.({ sum_price: sum });
      setRecalcOpen(false);
    },
    [onChange],
  );

  const handlePaymentCheckChange = useCallback(
    (checked: boolean) => {
      onChange?.({ payment: checked });
      if (checked) {
        paymentEmail.openPaymentEmail('payment');
      }
    },
    [onChange, paymentEmail],
  );

  const handleMemoBlockSave = useCallback(
    (newContent: string) => {
      if (memoBlockEditBlock == null) return;
      const updatedMemo = memoTimeline.updateMemoBlock(
        safe.memo || '',
        memoBlockEditBlock.originalIdx,
        newContent,
      );
      onChange?.({ memo: updatedMemo });
      setMemoBlockEditBlock(null);
    },
    [safe.memo, memoBlockEditBlock, onChange, memoTimeline],
  );

  const handleJsonSave = useCallback(
    (newJsonData: any) => {
      onChange?.({ json_data: newJsonData });
      setJsonModalOpen(false);
    },
    [onChange],
  );

  const handlePaymentEmailSend = useCallback(
    (emailData: { to: string; subject: string; body: string }) => {
      // 1. モーダルを即閉じ、トーストで「送信中」を表示
      paymentEmail.closePaymentEmail();
      setToast({ open: true, message: `${emailData.to} へメールを送信しています...`, severity: 'info', duration: null });

      // 2. 非同期で送信
      const url = `${sysApiBase}/bulk_send.php`;
      console.log('[Email] 送信URL:', url);
      console.log('[Email] payload:', emailData);
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
      })
        .then((res) => {
          console.log('[Email] レスポンスステータス:', res.status, res.statusText);
          console.log('[Email] レスポンスヘッダー:', [...res.headers.entries()]);
          return res.text();
        })
        .then((text) => {
          console.log('[Email] レスポンスボディ:', text.slice(0, 500));
          let result: any;
          try {
            result = JSON.parse(text);
          } catch {
            console.error('[Email] JSON parse失敗。レスポンス全文:', text);
            setToast({ open: true, message: 'メール送信エラー: サーバーから不正なレスポンスが返されました', severity: 'error', duration: 10000 });
            return;
          }
          if (result.success || result.status === 'success') {
            setToast({ open: true, message: 'メールを正常に送信しました', severity: 'success', duration: 5000 });
          } else {
            setToast({ open: true, message: 'メール送信に失敗しました: ' + (result.message || ''), severity: 'error', duration: 10000 });
          }
        })
        .catch((err) => {
          console.error('[Email] fetch失敗:', err.message, err);
          setToast({ open: true, message: `メール送信エラー: ${err.message}`, severity: 'error', duration: 10000 });
        });
    },
    [sysApiBase],
  );

  // --- Shared section props ---
  const sectionProps = {
    data: safe,
    onChange,
    disabled,
    isNew,
    onOpenEdit: genericEdit.handleOpenEdit,
    onOpenEditMulti: genericEdit.handleOpenEditMulti,
  };

  // --- Save button label ---
  const saveLabel = editor.isCopyReservation
    ? 'コピーする'
    : editor.isNewReservation
      ? '新規保存'
      : '保存';

  const panelTitle = isNew ? '新規予約' : `予約 - ${safe.page_id || safe.id}`;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`gc-sidepanel-backdrop ${panelVisible ? 'visible' : 'hidden'}`}
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Side panel */}
      <aside
        ref={panelRef}
        className={`gc-sidepanel ${panelVisible ? 'open' : 'closed'}`}
        role="dialog"
        aria-modal={panelVisible ? 'true' : 'false'}
        aria-hidden={panelVisible ? 'false' : 'true'}
      >
        {/* Sticky header */}
        <div className="gc-sidepanel-header">
          <div className="gc-sidepanel-title">
            <strong>{panelTitle}</strong>
          </div>
          <div className="gc-actions">
            <button
              type="button"
              className="gc-btn"
              onClick={onCancel}
              style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none' }}
            >
              閉じる
            </button>
            <button
              type="button"
              className={`gc-btn ${editor.isDirty ? 'gc-btn--primary' : 'gc-btn--secondary'}`}
              onClick={editor.handleSave}
              disabled={disabled || !editor.isDirty || editor.isSaving}
              style={{
                backgroundColor: editor.isDirty && !editor.isSaving ? '#3b82f6' : '#9ca3af',
                color: '#fff',
                cursor: editor.isDirty && !editor.isSaving ? 'pointer' : 'default',
              }}
            >
              {editor.isSaving ? <CircularProgress size={20} color="inherit" /> : saveLabel}
            </button>
          </div>
        </div>

        {/* Scrollable form body */}
        <div
          className="gc-form"
          style={{ padding: '0 16px', paddingBottom: '50px', display: 'flex', flexDirection: 'column' }}
        >
          {/* 1. 予約名・予約コード・確認同意・コメント・ショートメモ */}
          <BasicInfoSection {...sectionProps} />

          {/* 2. 確認・同意状況 */}
          <CheckFlagsSection {...sectionProps} onPaymentCheckChange={handlePaymentCheckChange} />

          {/* 3. メモ（タイムライン + アクションボタン） */}
          <MemoSection
            {...sectionProps}
            onOpenMemoBlockEdit={(block: MemoBlock) => setMemoBlockEditBlock(block)}
          />

          {/* 4. 利用期間・希望時間 */}
          <ScheduleSection {...sectionProps} />

          {/* 5. 連絡手段 */}
          <CommunicationSection {...sectionProps} />

          {/* 6. 予約コード・車両コード・車両タイプ・メール・電話 */}
          <VehicleSection
            {...sectionProps}
            vehicles={vehicles}
            vehicleTypes={vehicleTypes}
            onAssignClick={onAssignClick}
          />

          <CustomerSection {...sectionProps} />

          {/* 7. ステータス・ブッキング */}
          <StatusSection
            {...sectionProps}
            reservationStatusList={reservationStatusList}
            onStatusChange={handleStatusChange}
            isSavingStatus={statusChange.isSaving}
          />

          {/* 8. 料金 */}
          <PricingSection {...sectionProps} onRecalcClick={() => setRecalcOpen(true)} />

          {/* 9. オプション・その他 */}
          <OptionsSection {...sectionProps} />

          {/* 10. スタッフ */}
          <StaffSection {...sectionProps} />

          {/* 11. 緊急連絡先 */}
          <EmergencySection {...sectionProps} />

          {/* 12. 詳細設定（請求書・色・メールフラグ・メールテンプレート） */}
          {/* 12. メールテンプレート */}
          <EmailTemplateSection
            {...sectionProps}
            onOpenEmailTemplate={(type: string) => paymentEmail.openPaymentEmail(type)}
          />

          {/* 13. 詳細設定（請求書・メールフラグ） */}
          <DetailSettingsSection
            {...sectionProps}
            onInvoiceOpen={squareInvoice.openInvoiceLink}
            onInvoiceCreate={() => squareInvoice.createInvoice(editor.isDirty)}
            onInvoiceVoid={squareInvoice.voidInvoice}
            onJsonModalOpen={() => setJsonModalOpen(true)}
            isCreatingInvoice={squareInvoice.isCreatingInvoice}
            isVoidingInvoice={squareInvoice.isVoidingInvoice}
          />

          {/* 13. 背景色・テキスト色 */}
          <ColorSection {...sectionProps} />
        </div>
      </aside>

      {/* --- Modals --- */}
      <GenericEditDialog
        open={genericEdit.genericEditConfig !== null}
        config={genericEdit.genericEditConfig}
        onSave={genericEdit.handleSaveGenericEdit}
        onCancel={genericEdit.handleCancelGenericEdit}
        onValueChange={genericEdit.setGenericEditValue}
      />

      <MemoBlockEditModal
        open={memoBlockEditBlock !== null}
        block={memoBlockEditBlock}
        onSave={handleMemoBlockSave}
        onCancel={() => setMemoBlockEditBlock(null)}
      />

      <RecalcModal
        open={recalcOpen}
        data={safe}
        onApply={handleRecalcApply}
        onCancel={() => setRecalcOpen(false)}
      />

      <PaymentEmailModal
        open={paymentEmail.modalState.open}
        editing={safe}
        sysApiBase={sysApiBase}
        onClose={paymentEmail.closePaymentEmail}
        onSend={handlePaymentEmailSend}
        state={paymentEmail.modalState}
        onStateChange={paymentEmail.setModalState}
        onLangSelect={paymentEmail.handleLangSelect}
      />

      <JsonDataModal
        open={jsonModalOpen}
        jsonData={safe.json_data}
        onSave={handleJsonSave}
        onCancel={() => setJsonModalOpen(false)}
        disabled={disabled}
      />

      {/* ステータス変更確認ダイアログ（チェック初期化など） */}
      <Dialog
        open={statusChange.confirmDialog.open}
        onClose={() => statusChange.closeConfirmDialog(false)}
      >
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {statusChange.confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => statusChange.closeConfirmDialog(false)}>
            {statusChange.confirmDialog.noLabel || 'いいえ'}
          </Button>
          <Button onClick={() => statusChange.closeConfirmDialog(true)}>
            {statusChange.confirmDialog.yesLabel || 'はい'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.duration}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
